/**
 * 家具カテゴリ別の価格レンジ（円）
 */
const PRICE_RANGES = {
    desk: { min: 10000, max: 35000 },
    chair: { min: 12000, max: 40000 },
    bed: { min: 20000, max: 60000 },
    shelf: { min: 4000, max: 20000 },
    appliances: { min: 30000, max: 100000 },
    others: { min: 5000, max: 30000 },
    custom: { min: 3000, max: 10000 },
}

/**
 * 家具のカテゴリから価格レンジを取得
 * @param {string} category - 家具のカテゴリ (desk, chair, bed, shelf, custom)
 * @returns {{ min: number, max: number }} 価格レンジ
 */
export function getPriceRange(category) {
    return PRICE_RANGES[category] || PRICE_RANGES.custom
}

/**
 * 家具リストから合計価格レンジを計算
 * @param {Array} items - 家具リスト
 * @returns {{ totalMin: number, totalMax: number }} 合計価格レンジ
 */
export function calculateTotalPriceRange(items) {
    return items.reduce(
        (acc, item) => {
            const range = getPriceRange(item.category)
            return {
                totalMin: acc.totalMin + range.min,
                totalMax: acc.totalMax + range.max,
            }
        },
        { totalMin: 0, totalMax: 0 }
    )
}

/**
 * 価格をフォーマット（カンマ区切り）
 * @param {number} price - 価格
 * @returns {string} フォーマット済み価格
 */
export function formatPrice(price) {
    return `¥${price.toLocaleString()}`
}

/**
 * 価格レンジをフォーマット
 * @param {{ min: number, max: number }} range - 価格レンジ
 * @returns {string} フォーマット済みレンジ
 */
export function formatPriceRange(range) {
    return `${formatPrice(range.min)} 〜 ${formatPrice(range.max)}`
}

/**
 * カテゴリ・サブタイプから検索用の日本語名を取得
 * @param {string} category - 家具のカテゴリ
 * @param {string} subtype - 家具のサブタイプ
 * @returns {string|null} 検索用の名前（カスタムはnull）
 */
export function getSearchKeyword(category, subtype) {
    // カスタムは検索対象外
    if (category === 'custom') return null

    // サブタイプ別の名前マッピング
    const subtypeNames = {
        desk: {
            default: 'デスク',
            pc: 'パソコンデスク',
            lowTable: 'ローテーブル',
        },
        chair: {
            default: 'チェア',
            desk: 'デスクチェア',
            gaming: 'ゲーミングチェア',
            sofa: 'ソファ',
            floor: '座椅子',
        },
        bed: {
            default: 'ベッド',
            standard: 'ベッド',
            mattress: 'マットレス',
            loft: 'ロフトベッド',
        },
        shelf: {
            default: '棚',
            bookshelf: '本棚',
            storage: 'カラーボックス',
            tvStand: 'テレビ台',
            hanger: 'ハンガーラック',
        },
        appliances: {
            default: '冷蔵庫',
            refrigerator: '冷蔵庫',
            washer: '洗濯機',
        },
        others: {
            default: '観葉植物',
            mirror: '全身鏡',
            plant: '観葉植物',
        },
    }

    const categoryMap = subtypeNames[category]
    if (!categoryMap) return null

    // サブタイプがある場合はそれを使用、なければdefault
    return categoryMap[subtype] || categoryMap['default'] || null
}

/**
 * 楽天検索URLを生成（家具情報から）
 * @param {object} furniture - 家具オブジェクト { category, subtype }
 * @returns {string|null} 楽天検索URL（カスタムはnull）
 */
export function getRakutenSearchUrlForFurniture(furniture) {
    const keyword = getSearchKeyword(furniture.category, furniture.subtype)
    if (!keyword) return null

    // 幅を10cm単位で切り捨て（size.widthを参照）
    const width = furniture.size?.width || furniture.width || 100
    const roundedWidth = Math.floor(width / 10) * 10

    // アフィリエイトID
    const affiliateId = '4ffcbd54.93e164fb.4ffcbd55.92e788c1'

    // 検索クエリを作成（「デスク 120cm」形式）
    const searchQuery = `${keyword} ${roundedWidth}cm`
    const encodedKeyword = encodeURIComponent(searchQuery)

    // アフィリエイトリンク形式
    const searchUrl = `https://search.rakuten.co.jp/search/mall/${encodedKeyword}/`
    return `https://hb.afl.rakuten.co.jp/hgc/${affiliateId}/?pc=${encodeURIComponent(searchUrl)}`
}

/**
 * 楽天検索URLを生成（キーワードから・後方互換用）
 * @param {string} keyword - 検索キーワード
 * @returns {string} 楽天検索URL
 */
export function getRakutenSearchUrl(keyword) {
    const affiliateId = '4ffcbd54.93e164fb.4ffcbd55.92e788c1'
    const encodedKeyword = encodeURIComponent(keyword)
    const searchUrl = `https://search.rakuten.co.jp/search/mall/${encodedKeyword}/`
    return `https://hb.afl.rakuten.co.jp/hgc/${affiliateId}/?pc=${encodeURIComponent(searchUrl)}`
}
